import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import api from "../../api/axiosInstance";
import {
  PeopleOutline as PeopleIcon,
  PersonAddAlt1Outlined as PersonAddIcon,
  BadgeOutlined as BadgeIcon,
  BusinessCenterOutlined as BusinessIcon,
} from "@mui/icons-material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import Badge from "../../components/Badge";
import { ACTIVE_STATUSES } from "../AdminManageOpportunities/opportunityOptions";

const buildSummaryCards = (stats) => {
  if (!stats) return [];
  const { userCount, candidateCount, employeeCount, subconsultantCount } = stats;
  const total = userCount || 0;
  const pct = (n) => (total > 0 ? ((n / total) * 100).toFixed(1) : 0);
  return [
    {
      icon: PeopleIcon,
      value: userCount,
      label: "Total Users",
      sublabel: "Active users in the system",
    },
    {
      icon: PersonAddIcon,
      value: candidateCount,
      label: "Candidates",
      sublabel: `${pct(candidateCount)}% of total users`,
    },
    {
      icon: BadgeIcon,
      value: employeeCount,
      label: "Employees",
      sublabel: `${pct(employeeCount)}% of total users`,
    },
    {
      icon: BusinessIcon,
      value: subconsultantCount,
      label: "Sub-consultants",
      sublabel: `${pct(subconsultantCount)}% of total users`,
    },
  ];
};

const ROLE_CHART_COLORS = [
  "#2196F3", "#FF9800", "#00BCD4", "#E91E63", "#9C27B0",
  "#4CAF50", "#795548", "#607D8B", "#F44336", "#009688",
];

const buildRoleChartData = (roleDistribution) => {
  if (!roleDistribution || typeof roleDistribution !== "object") return [];
  const entries = Object.entries(roleDistribution).filter(([, count]) => count > 0);
  const total = entries.reduce((sum, [, c]) => sum + c, 0);
  if (total === 0) return [];
  return entries.map(([name, count], i) => ({
    name,
    value: parseFloat(((count / total) * 100).toFixed(2)),
    color: ROLE_CHART_COLORS[i % ROLE_CHART_COLORS.length],
  }));
};

const buildSeniorityChartData = (seniorityDistribution) => {
  if (!seniorityDistribution || typeof seniorityDistribution !== "object") return [];
  const entries = Object.entries(seniorityDistribution).filter(([, count]) => count > 0);
  const total = entries.reduce((sum, [, c]) => sum + c, 0);
  if (total === 0) return [];
  return entries.map(([name, count], i) => ({
    name,
    value: parseFloat(((count / total) * 100).toFixed(2)),
    color: ROLE_CHART_COLORS[i % ROLE_CHART_COLORS.length],
  }));
};

const formatRelativeTime = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ago`;
};

// Every status except Lost (Opportunities 2c).
const ACTIVE_OPPORTUNITY_STATUSES = ACTIVE_STATUSES;

const formatDueDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
};

const SummaryCard = ({ icon: Icon, value, label, sublabel }) => (
  <Card>
    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <Typography variant="body2">
          {label}
        </Typography>
        <Badge>
          <Icon />
        </Badge>
      </Box>
      <Typography variant="xl">
        {value}
      </Typography>
      <Typography variant="sm" color="text.secondary" component="p">
        {sublabel}
      </Typography>
    </CardContent>
  </Card>
);

const DashboardCard = ({ title, subtitle, children }) => (
  <Card>
    <CardContent sx={{ "&:last-child": { pb: 2 } }}>
      <Typography variant="xl" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
      <hr style={{ borderColor: '#EAE7E6' }} />
      {children}
    </CardContent>
  </Card>
);

const ChartCard = ({ title, subtitle, data }) => (
  <DashboardCard title={title} subtitle={subtitle} >
      <Box sx={{ display: "flex", alignItems: "center", height: 240 }}>
        <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
                label={({ name, value }) => `${name}: ${value}%`}
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ minWidth: 140, pl: 1 }}>
          {data.map((entry, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                  mr: 1,
                }}
              />
              <Typography variant="body2">{entry.name}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
  </DashboardCard>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setStatsLoading(true);
        setStatsError(null);
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (err) {
        setStatsError(err.response?.data?.error || err.message || "Failed to load stats");
        setStats({ userCount: 0, candidateCount: 0, employeeCount: 0, subconsultantCount: 0, roleDistribution: {}, seniorityDistribution: {} });
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
  }, []);

  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activeOpportunities, setActiveOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(true);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setActivityLoading(true);
        const { data } = await api.get("/admin/activity");
        setRecentActivity(data || []);
      } catch (err) {
        console.error("Failed to fetch activity:", err);
        setRecentActivity([]);
      } finally {
        setActivityLoading(false);
      }
    };
    fetchActivity();
  }, []);

  useEffect(() => {
    const fetchActiveOpportunities = async () => {
      try {
        setOpportunitiesLoading(true);
        const params = new URLSearchParams({
          status: ACTIVE_OPPORTUNITY_STATUSES.join(","),
        });
        const { data } = await api.get(`/opportunities/?${params}`);
        setActiveOpportunities(data || []);
      } catch (err) {
        console.error("Failed to fetch active opportunities:", err);
        setActiveOpportunities([]);
      } finally {
        setOpportunitiesLoading(false);
      }
    };
    fetchActiveOpportunities();
  }, []);

  const summaryCards = buildSummaryCards(stats);
  const roleChartData = buildRoleChartData(stats?.roleDistribution);
  const seniorityChartData = buildSeniorityChartData(stats?.seniorityDistribution);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="displaySm">
        Dashboard
      </Typography>

      {/* Summary cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 120 }}>
                <CircularProgress size={32} />
              </CardContent>
            </Card>
          ))
        ) : statsError ? (
          <Typography color="error" sx={{ gridColumn: "1 / -1" }}>
            {statsError}
          </Typography>
        ) : (
          summaryCards.map((card) => (
            <SummaryCard key={card.label} {...card} />
          ))
        )}
      </Box>

      {/* Charts row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <ChartCard
          title="Users by Main Role"
          subtitle="Distribution of candidates by main role"
          data={roleChartData.length > 0 ? roleChartData : [{ name: "No data", value: 100, color: "#E0E0E0" }]}
        />
        <ChartCard
          title="Users by Seniority Level"
          subtitle="Distribution of candidates by seniority level"
          data={seniorityChartData.length > 0 ? seniorityChartData : [{ name: "No data", value: 100, color: "#E0E0E0" }]}
        />
      </Box>

      {/* Activity & Opportunities */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" },
          gap: 2,
        }}
      >
        <DashboardCard title="Recent Activity" subtitle="Recent activity in the system">
          <List dense disablePadding>
            {activityLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : recentActivity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No recent activity
              </Typography>
            ) : (
              recentActivity.map((item, index) => (
              <ListItem
                key={index}
                sx={{
                  alignItems: "flex-start",
                  px: 0,
                  borderBottom:
                    index < recentActivity.length - 1
                      ? "1px solid #EAE7E6"
                      : "none",
                  pb: index < recentActivity.length - 1 ? 1.5 : 0,
                  mb: index < recentActivity.length - 1 ? 1.5 : 0,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "#DEDFD1",
                    mt: 1.2,
                    mr: 1.5,
                    flexShrink: 0,
                  }}
                />
                <ListItemText
                  primary={item.action}
                  secondary={`${item.detail}${item.createdAt ? ` - ${formatRelativeTime(item.createdAt)}` : ""}`}
                  primaryTypographyProps={{ fontWeight: 500, fontSize: "0.9375rem" }}
                  secondaryTypographyProps={{ fontSize: "0.8125rem", color: "text.secondary" }}
                />
              </ListItem>
            )))}
          </List>
        </DashboardCard>

        <DashboardCard title="Active Opportunities" subtitle="Active opportunities in the system">
          <List dense disablePadding>
            {opportunitiesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : activeOpportunities.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No active opportunities
              </Typography>
            ) : (
              activeOpportunities.map((opp, index) => (
              <ListItem
                key={opp._id}
                sx={{
                  alignItems: "flex-start",
                  px: 0,
                  borderBottom:
                    index < activeOpportunities.length - 1
                      ? "1px solid #EAE7E6"
                      : "none",
                  pb: index < activeOpportunities.length - 1 ? 1.5 : 0,
                  mb: index < activeOpportunities.length - 1 ? 1.5 : 0,
                }}
              >
                <Badge size={54}>
                  <BusinessIcon />
                </Badge>
                <ListItemText
                  primary={opp.name}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" display="block">
                        {opp.client?.name ?? "—"}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.secondary">
                        Due date: {formatDueDate(opp.dueDate)}
                      </Typography>
                    </>
                  }
                  sx={{ ml: 1.5 }}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            )))}
          </List>
        </DashboardCard>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
